import { FormattedMessage } from "react-intl";

import { RanaHeading } from "@nens/ranalib";
import * as api from "@nens/threedi/openapi-client";

import NumberInput from "../../components/NumberInput";
import inputElementStyles from "../../styles/InputElements.module.css";
import styles from "../ExportMenu.module.css";

import ExportRow from "./ExportRow";
import ExportRowMain from "./ExportRowMain";

interface Props {
  setDamageEstimation: (bool: boolean) => void;
  setDamageCostType: (costType: api.CostTypeEnum) => void;
  setDamageFloodMonth: (floodMonth: api.FloodMonthEnum) => void;
  setDamageInundation: (inundationPeriod: number) => void;
  setDamageInfrastructure: (repairTime: number) => void;
  setDamageBuildings: (repairTime: number) => void;

  damageEstimation: boolean;
  costType: api.CostTypeEnum;
  floodMonth: api.FloodMonthEnum;
  inundationPeriod: number;
  repairTimeBuildings: number;
  repairTimeInfrastructure: number;
  damageOptionChecked: boolean;
  basicOption: boolean;
}

const ExportDamageEstimation: React.FC<Props> = (props) => {
  const { costType, floodMonth, inundationPeriod, repairTimeBuildings, repairTimeInfrastructure } =
    props;

  return (
    <div className={styles.DamageEstimation}>
      <ExportRow>
        <ExportRowMain
          selected={props.damageEstimation}
          disabled={!props.basicOption}
          onClick={() => {
            if (props.basicOption) {
              props.setDamageEstimation(!props.damageEstimation);
            }
          }}
          exportImage={{
            src: "./assets/images/export-damage.png",
            alt: "Damage estimation",
          }}
        >
          <RanaHeading type="h5" size="h5" fontWeight="semibold" mb="8px">
            <FormattedMessage
              id="export_modal.export_text_damage_title"
              defaultMessage="Damage estimation"
            />
          </RanaHeading>
          <p>
            <FormattedMessage
              id="export_modal.export_text_damage_content"
              defaultMessage="Basic processed results and damage estimation. Only available for results in the Netherlands"
            />
          </p>
        </ExportRowMain>
        {props.damageEstimation ? (
          <div className={`${styles.DamageEstimationDetails} ${styles.ExportRowDamage}`}>
            <div className={styles.DamageEstimationRow}>
              <span>
                <FormattedMessage id="exportMenu.damage_cost_type" defaultMessage="Cost type" />:
              </span>
              <span className={styles.Input}>
                <select
                  className={`${inputElementStyles.Padding} ${inputElementStyles.Input} ${styles.Input}`}
                  value={costType}
                  onChange={(e) => {
                    const costType = e.target.value as api.CostTypeEnum;
                    props.setDamageCostType(costType);
                  }}
                  disabled={!props.damageEstimation}
                >
                  <option value={api.CostTypeEnum.MIN}>Min</option>
                  <option value={api.CostTypeEnum.AVG}>Average</option>
                  <option value={api.CostTypeEnum.MAX}>Max</option>
                </select>
              </span>
            </div>
            <div className={styles.DamageEstimationRow}>
              <span>
                <FormattedMessage id="exportMenu.damage_flood_month" defaultMessage="Flood month" />
                :
              </span>
              <span className={styles.Input}>
                <select
                  className={`${inputElementStyles.Padding} ${inputElementStyles.Input} ${styles.Input}`}
                  value={floodMonth}
                  onChange={(e) => {
                    const floodMonth = e.target.value as api.FloodMonthEnum;
                    props.setDamageFloodMonth(floodMonth);
                  }}
                  disabled={!props.damageEstimation}
                >
                  {[
                    [api.FloodMonthEnum.JAN, "January"],
                    [api.FloodMonthEnum.FEB, "February"],
                    [api.FloodMonthEnum.MAR, "March"],
                    [api.FloodMonthEnum.APR, "April"],
                    [api.FloodMonthEnum.MAY, "May"],
                    [api.FloodMonthEnum.JUN, "June"],
                    [api.FloodMonthEnum.JUL, "July"],
                    [api.FloodMonthEnum.AUG, "August"],
                    [api.FloodMonthEnum.SEP, "September"],
                    [api.FloodMonthEnum.OCT, "October"],
                    [api.FloodMonthEnum.NOV, "November"],
                    [api.FloodMonthEnum.DEC, "December"],
                  ].map((month) => (
                    <option key={month[0]} value={month[0]}>
                      {month[1]}
                    </option>
                  ))}
                </select>
              </span>
            </div>
            <div className={styles.DamageEstimationRow}>
              <span>
                <FormattedMessage
                  id="exportMenu.damage_inundation"
                  defaultMessage="Time of inundation (hours)"
                />
                :
              </span>
              <span className={styles.Input}>
                <NumberInput
                  value={inundationPeriod + ""}
                  unit={
                    <FormattedMessage id="newRainMenu.duration_unit_hour" defaultMessage="hours" />
                  }
                  setValue={(stringValue: string) =>
                    props.setDamageInundation(parseInt(stringValue, 10) || 0)
                  }
                  disabled={!props.damageEstimation}
                  required
                />
              </span>
            </div>
            <div className={styles.DamageEstimationRow}>
              <span>
                <FormattedMessage
                  id="exportMenu.damage_repair_time_buildings"
                  defaultMessage="Repair time of buildings"
                />
                :
              </span>
              <span className={styles.Input}>
                <select
                  className={`${inputElementStyles.Padding} ${inputElementStyles.Input} ${styles.Input}`}
                  value={repairTimeBuildings}
                  onChange={(e) => props.setDamageBuildings(Number(e.target.value))}
                  disabled={!props.damageEstimation}
                >
                  {/* max is 240 hours ~ 10 days */}
                  <option value={0}>0 hour</option>
                  <option value={6}>6 hours</option>
                  <option value={24}>1 day</option>
                  <option value={48}>2 days</option>
                  <option value={120}>5 days</option>
                  <option value={240}>10 days</option>
                </select>
              </span>
            </div>
            <div className={styles.DamageEstimationRow}>
              <span>
                <FormattedMessage
                  id="exportMenu.damage_repair_time_infastructure"
                  defaultMessage="Repair time of infrastructure"
                />
                :
              </span>
              <span className={styles.Input}>
                <select
                  className={`${inputElementStyles.Padding} ${inputElementStyles.Input} ${styles.Input}`}
                  value={repairTimeInfrastructure}
                  onChange={(e) => props.setDamageInfrastructure(Number(e.target.value))}
                  disabled={!props.damageEstimation}
                >
                  {/* max is 240 hours ~ 10 days */}
                  <option value={0}>0 hour</option>
                  <option value={6}>6 hours</option>
                  <option value={24}>1 day</option>
                  <option value={48}>2 days</option>
                  <option value={120}>5 days</option>
                  <option value={240}>10 days</option>
                </select>
              </span>
            </div>
          </div>
        ) : null}
      </ExportRow>
    </div>
  );
};

export default ExportDamageEstimation;
